import React from "react";
import { Image as ImageIcon, Crown, Workflow, Database, Mail } from "lucide-react";
import SectionHeading from "./SectionHeading";

const CAPS = [
  {
    icon: ImageIcon,
    title: "AI Photo Generation",
    headline: "Dynamic branded content every guest wants to share.",
    desc:
      "Stori generates branded photo templates on-the-fly using AI — not static overlays. Every guest gets unique, personalized content engineered for social.",
    stat: "210%",
    statLabel: "more social shares vs. static booths",
    span: "lg:col-span-2 lg:row-span-2",
    accent: true,
  },
  {
    icon: Crown,
    title: "Influencer Detection",
    headline: "Spot high-value guests. Engage them differently.",
    desc:
      "AI detects social reach, audience size, and engagement patterns in real-time. Flagged influencers trigger VIP playbooks instantly.",
    stat: "28%",
    statLabel: "of guests identified as influencers",
    span: "lg:col-span-1",
  },
  {
    icon: Workflow,
    title: "Marketing Playbooks",
    headline: "Your marketing runs itself, by guest type.",
    desc:
      "Pre-built workflows fire automatically — VIP, Family, Business Traveler, Standard. Different sequences for different guests.",
    stat: "3.2x",
    statLabel: "higher follow-up conversion",
    span: "lg:col-span-1",
  },
  {
    icon: Database,
    title: "CRM Integration",
    headline: "Guest data flows into your stack automatically.",
    desc:
      "Sync to Salesforce, HubSpot, and more. Real-time, segmented, first-party data — yours to keep, GDPR + CCPA compliant.",
    stat: "62%",
    statLabel: "average email capture rate",
    span: "lg:col-span-2",
  },
  {
    icon: Mail,
    title: "Post-Engagement Automation",
    headline: "Sequences trigger after they leave the room.",
    desc:
      "Welcome email within 1 hour, targeted offer in 24, ongoing nurture and retargeting — all wired to guest classification.",
    stat: "38%",
    statLabel: "return visit rate (vs. ~10% baseline)",
    span: "lg:col-span-3",
  },
];

export default function Capabilities() {
  return (
    <section
      id="capabilities"
      data-testid="capabilities-section"
      className="relative py-24 lg:py-32 border-b border-stori-border"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <SectionHeading
          eyebrow="WHAT STORI'S AI ACTUALLY DOES"
          title="Five capabilities. One defensible moat."
          lead="Photo booth features are easily copied. AI + Data + Automation isn't."
        />

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-3 gap-4 lg:auto-rows-[260px]">
          {CAPS.map((c, i) => {
            const Icon = c.icon;
            return (
              <article
                key={i}
                data-testid={`capability-${i}`}
                className={`group relative p-7 lg:p-8 rounded-2xl border border-stori-border bg-stori-surface1 hover:border-stori-cyan/50 transition-all overflow-hidden flex flex-col ${c.span}`}
              >
                {c.accent && (
                  <div className="absolute -top-24 -right-24 w-64 h-64 bg-stori-cyan/10 rounded-full blur-3xl pointer-events-none" />
                )}
                <div className="relative flex items-center justify-between">
                  <div
                    className={`w-11 h-11 rounded-lg border flex items-center justify-center ${
                      c.accent
                        ? "border-stori-cyan text-stori-cyan bg-stori-cyan/5"
                        : "border-stori-border text-white"
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                  </div>
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-stori-muted">
                    Capability · 0{i + 1}
                  </span>
                </div>

                <div className="relative mt-6 flex-1 flex flex-col">
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-stori-cyan">
                    {c.title}
                  </div>
                  <h3 className="mt-2 font-heading text-2xl lg:text-3xl text-white font-semibold leading-tight">
                    {c.headline}
                  </h3>
                  <p className="mt-3 text-sm lg:text-base text-stori-muted leading-relaxed">
                    {c.desc}
                  </p>
                </div>

                <div className="relative mt-6 pt-5 border-t border-stori-border flex items-baseline gap-3">
                  <span className="font-heading text-3xl font-semibold text-stori-cyan cyan-text-glow">
                    {c.stat}
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-stori-muted">
                    {c.statLabel}
                  </span>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
